/**
 * Constants for the Squad Planning feature.
 *
 * Centralises formation options, tier definitions, position-to-slot
 * mappings, and field rendering defaults so they are easy to find
 * and update in one place.
 * @module features/squad-planning/constants
 */

import type { PitchPlayerOrigin } from "@/features/scouting/hooks/usePitchPlayerNavigation";
import { ShadowTeamFormation } from "@/generated/graphql";

import type {
  ColumnSortMetric,
  ColumnViewTierName,
  FormationOption,
  SquadPlanningFormation,
  TierName,
} from "./types";

// ---------------------------------------------------------------------------
// Formations
// ---------------------------------------------------------------------------

/** Formation options available in Squad Planning, grouped by defensive line. */
export const FORMATION_OPTIONS: readonly FormationOption[] = [
  // 3 at the back
  {
    value: ShadowTeamFormation.F_334 as SquadPlanningFormation,
    label: "3-3-4",
  },
  {
    value: ShadowTeamFormation.F_343 as SquadPlanningFormation,
    label: "3-4-3",
  },
  {
    value: ShadowTeamFormation.F_352 as SquadPlanningFormation,
    label: "3-5-2",
  },
  {
    value: ShadowTeamFormation.F_3412 as SquadPlanningFormation,
    label: "3-4-1-2",
  },
  // 4 at the back
  {
    value: ShadowTeamFormation.F_424 as SquadPlanningFormation,
    label: "4-2-4",
  },
  {
    value: ShadowTeamFormation.F_433 as SquadPlanningFormation,
    label: "4-3-3",
  },
  {
    value: ShadowTeamFormation.F_442 as SquadPlanningFormation,
    label: "4-4-2",
  },
  {
    value: ShadowTeamFormation.F_451 as SquadPlanningFormation,
    label: "4-5-1",
  },
  {
    value: ShadowTeamFormation.F_4123 as SquadPlanningFormation,
    label: "4-1-2-3",
  },
  {
    value: ShadowTeamFormation.F_4132 as SquadPlanningFormation,
    label: "4-1-3-2",
  },
  {
    value: ShadowTeamFormation.F_4213 as SquadPlanningFormation,
    label: "4-2-1-3",
  },
  {
    value: ShadowTeamFormation.F_4222 as SquadPlanningFormation,
    label: "4-2-2-2",
  },
  {
    value: ShadowTeamFormation.F_4231 as SquadPlanningFormation,
    label: "4-2-3-1",
  },
  {
    value: ShadowTeamFormation.F_4312 as SquadPlanningFormation,
    label: "4-3-1-2",
  },
  {
    value: ShadowTeamFormation.F_4321 as SquadPlanningFormation,
    label: "4-3-2-1",
  },
  {
    value: ShadowTeamFormation.F_4411 as SquadPlanningFormation,
    label: "4-4-1-1",
  },
  // 5 at the back
  {
    value: ShadowTeamFormation.F_532 as SquadPlanningFormation,
    label: "5-3-2",
  },
  {
    value: ShadowTeamFormation.F_541 as SquadPlanningFormation,
    label: "5-4-1",
  },
  {
    value: ShadowTeamFormation.F_5221 as SquadPlanningFormation,
    label: "5-2-2-1",
  },
] as const;

/** Default formation when org has no preference set. */
export const DEFAULT_FORMATION: SquadPlanningFormation =
  ShadowTeamFormation.F_433;

/**
 * Maps a ShadowTeamFormation enum value to its digit string.
 * E.g. F_433 → "433", F_4231 → "4231".
 */
export const FORMATION_TO_DIGITS: Readonly<
  Record<SquadPlanningFormation, string>
> = {
  [ShadowTeamFormation.F_334]: "334",
  [ShadowTeamFormation.F_343]: "343",
  [ShadowTeamFormation.F_352]: "352",
  [ShadowTeamFormation.F_3412]: "3412",
  [ShadowTeamFormation.F_424]: "424",
  [ShadowTeamFormation.F_433]: "433",
  [ShadowTeamFormation.F_442]: "442",
  [ShadowTeamFormation.F_451]: "451",
  [ShadowTeamFormation.F_4123]: "4123",
  [ShadowTeamFormation.F_4132]: "4132",
  [ShadowTeamFormation.F_4213]: "4213",
  [ShadowTeamFormation.F_4222]: "4222",
  [ShadowTeamFormation.F_4231]: "4231",
  [ShadowTeamFormation.F_4312]: "4312",
  [ShadowTeamFormation.F_4321]: "4321",
  [ShadowTeamFormation.F_4411]: "4411",
  [ShadowTeamFormation.F_532]: "532",
  [ShadowTeamFormation.F_541]: "541",
  [ShadowTeamFormation.F_5221]: "5221",
};

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

/** Ordered list of tier names (rendering order top → bottom). */
export const TIER_ORDER: readonly TierName[] = [
  "First Team",
  "Rotation",
  "Academy",
  "Targets",
] as const;

/**
 * Tier names for the column view layout.
 * @remarks
 * Rotation players are merged into First Team, and Targets is a
 * placeholder column for scouting list players (not yet populated).
 */
export const COLUMN_VIEW_TIERS: readonly ColumnViewTierName[] = [
  "First Team",
  "Academy",
  "Targets",
] as const;

/**
 * Sort metrics available per tier in the Columns view (SE-5342).
 * Academy excludes GPR and Minutes (not meaningful for academy players) per
 * the V1.6 PRD Section 6; First Team and Targets expose the full set.
 */
export const COLUMN_SORT_METRICS_BY_TIER: Readonly<
  Record<ColumnViewTierName, readonly ColumnSortMetric[]>
> = {
  "First Team": ["minutes", "age", "gpr", "wage", "name"],
  Academy: ["age", "wage", "name"],
  Targets: ["minutes", "age", "gpr", "wage", "name"],
};

/**
 * Default sort direction applied when a metric is first selected. Performance
 * metrics default to descending (best/most first); age and name ascending.
 */
export const COLUMN_SORT_DEFAULT_DIRECTION: Readonly<
  Record<ColumnSortMetric, "asc" | "desc">
> = {
  minutes: "desc",
  age: "asc",
  gpr: "desc",
  wage: "desc",
  name: "asc",
};

/**
 * Three-tier order for the depth panel (table view).
 * Rotation players are merged into First Team at the data layer.
 */
export const DEPTH_PANEL_TIERS: readonly ColumnViewTierName[] = [
  "First Team",
  "Academy",
  "Targets",
] as const;

/**
 * Maps tier names to their depth-panel testID suffixes.
 * Used to generate `data-testid="depth-panel-first-team"` etc.
 */
export const DEPTH_PANEL_TEST_IDS: Readonly<
  Record<ColumnViewTierName, string>
> = {
  "First Team": "depth-panel-first-team",
  Academy: "depth-panel-academy",
  Targets: "depth-panel-targets",
};

// ---------------------------------------------------------------------------
// Position → Formation Slot Mapping
// ---------------------------------------------------------------------------

/**
 * Maps player position abbreviations (from the database) to compatible
 * formation slot roles. Used to assign players to position nodes.
 */
export const POSITION_TO_SLOT_GROUP: Readonly<
  Record<string, readonly string[]>
> = {
  GK: ["GK"],
  CB: ["CB", "LCB", "RCB"],
  LB: ["LB", "LWB"],
  RB: ["RB", "RWB"],
  LWB: ["LB", "LWB"],
  RWB: ["RB", "RWB"],
  CDM: ["CDM", "CM", "LCDM", "RCDM"],
  CM: ["CM", "CDM", "LCM", "RCM", "LCDM", "RCDM"],
  LM: ["LM"],
  RM: ["RM"],
  CAM: ["CAM", "CM"],
  LW: ["LW"],
  RW: ["RW"],
  ST: ["ST", "LST", "RST"],
  CF: ["ST", "CAM", "LST", "RST"],
};

// ---------------------------------------------------------------------------
// Position Role Display Names
// ---------------------------------------------------------------------------

/**
 * Maps role abbreviations to human-readable full names for display in
 * the position filter pill (e.g. "Striker - ST").
 */
export const ROLE_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  GK: "Goalkeeper",
  LB: "Left-Back",
  LWB: "Left Wing-Back",
  RB: "Right-Back",
  RWB: "Right Wing-Back",
  CB: "Centre-Back",
  LCB: "Left Centre-Back",
  RCB: "Right Centre-Back",
  CDM: "Defensive Midfielder",
  LCDM: "Left Defensive Midfielder",
  RCDM: "Right Defensive Midfielder",
  CM: "Centre Midfielder",
  LCM: "Left Centre Midfielder",
  RCM: "Right Centre Midfielder",
  CAM: "Attacking Midfielder",
  LM: "Left Midfielder",
  RM: "Right Midfielder",
  LW: "Left Wing",
  RW: "Right Wing",
  ST: "Striker",
  LST: "Left Striker",
  RST: "Right Striker",
};

// ---------------------------------------------------------------------------
// Player Actions
// ---------------------------------------------------------------------------

/** All player actions available in scenario mode. */
export const PLAYER_ACTIONS = ["Sign", "Sell", "Loan", "Promote"] as const;

// ---------------------------------------------------------------------------
// Readiness Tag → Badge Variant
// ---------------------------------------------------------------------------

/** Badge action variants for readiness tags. */
export type ReadinessBadgeAction = "success" | "warning" | "error" | "muted";

/** Maps readiness values to GlueStack Badge action variants. */
export const READINESS_BADGE_ACTION: Readonly<
  Record<string, ReadinessBadgeAction>
> = {
  Fit: "success",
  Recovering: "warning",
  Injured: "error",
};

// ---------------------------------------------------------------------------
// Scenario Pitch → player page
// ---------------------------------------------------------------------------

/**
 * SE-8605: a name on the scenario Pitch opens the player page behind the
 * Table view's trail (PlayerPanelContainer's onRowPress).
 */
export const SCENARIO_PITCH_PLAYER_ORIGIN: PitchPlayerOrigin = {
  label: "Back to Squad",
  path: "/squad",
  source: "squad-planning",
};
